// src/pages/TemplateSelection.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './TemplateSelection.css';

const TemplateSelection = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  
  // Cargar plantillas al iniciar
  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoading(true);
      
      try {
        // En una aplicación real, aquí haríamos una llamada a la API
        // Para este ejemplo, usamos datos de muestra
        
        // Simular una respuesta de API
        setTimeout(() => {
          const mockTemplates = [
            {
              id: 1,
              name: "Asistente Comercial",
              bot_type: "commercial",
              description: "Perfecto para atención al cliente y ventas. Responde consultas, proporciona información de productos y deriva a agentes cuando es necesario.",
              input_providers: ["Email", "WhatsApp"],
              action_providers: ["CRM Salesforce"],
              output_providers: ["Email", "WhatsApp"]
            },
            {
              id: 2,
              name: "Agente Asistente Personal",
              bot_type: "personal",
              description: "Ayuda con la gestión de calendario, recordatorios y tareas personales.",
              input_providers: ["Email", "Telegram"],
              action_providers: ["Google Calendar", "Trello"],
              output_providers: ["Email", "Telegram"]
            },
            {
              id: 3,
              name: "Bot de Soporte Web",
              bot_type: "website",
              description: "Diseñado para integrarse en tu sitio web y proporcionar soporte inmediato a los visitantes.",
              input_providers: ["Chat Web"],
              action_providers: ["CRM Salesforce", "Sheets"],
              output_providers: ["Chat Web"]
            },
            {
              id: 4,
              name: "Asistente de Reservas",
              bot_type: "commercial",
              description: "Gestiona reservas y citas para tu negocio de forma automatizada.",
              input_providers: ["Email", "WhatsApp", "Telegram"],
              action_providers: ["Google Calendar", "CRM Salesforce"],
              output_providers: ["Email", "WhatsApp", "Telegram"]
            },
            {
              id: 5,
              name: "Asistente de Marketing",
              bot_type: "commercial",
              description: "Automatiza campañas de marketing y seguimiento de leads.",
              input_providers: ["Email"],
              action_providers: ["Mailchimp", "Sheets"],
              output_providers: ["Email"]
            },
            {
              id: 6,
              name: "Chatbot para E-commerce",
              bot_type: "website",
              description: "Ayuda a los clientes a encontrar productos, responde preguntas frecuentes y procesa pedidos.",
              input_providers: ["Chat Web"],
              action_providers: ["Shopify", "Sheets"],
              output_providers: ["Chat Web"]
            }
          ];
          
          setTemplates(mockTemplates);
          setIsLoading(false);
        }, 1200);
        
      } catch (error) {
        console.error("Error al cargar las plantillas:", error);
        setIsLoading(false);
      }
    };
    
    fetchTemplates();
  }, []);

  // Filtrar las plantillas según búsqueda y tipo
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          template.description.toLowerCase().includes(searchTerm.toLowerCase());
                          
    const matchesType = selectedType === 'all' || template.bot_type === selectedType;
    
    return matchesSearch && matchesType;
  });

  // Función para seleccionar una plantilla y redirigir al wizard
  const selectTemplate = (templateId) => {
    navigate(`/bot-creation/${templateId}`);
  };
  
  // Función para ir a la creación de bot sin plantilla
  const createCustomBot = () => {
    navigate('/bot-creation');
  };

  return (
    <div className="template-selection-container">
      <h1>Selecciona una Plantilla</h1>
      <p className="description">
        Elige una plantilla predefinida para tu bot o crea uno personalizado desde cero.
      </p>
      
      <div className="filter-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar plantillas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-buttons">
          <button
            className={`filter-btn ${selectedType === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedType('all')}
          >
            Todos
          </button>
          <button
            className={`filter-btn ${selectedType === 'commercial' ? 'active' : ''}`}
            onClick={() => setSelectedType('commercial')}
          >
            Comerciales
          </button>
          <button
            className={`filter-btn ${selectedType === 'personal' ? 'active' : ''}`}
            onClick={() => setSelectedType('personal')}
          >
            Personales
          </button>
          <button
            className={`filter-btn ${selectedType === 'website' ? 'active' : ''}`}
            onClick={() => setSelectedType('website')}
          >
            Web
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="templates-loading">
          <div className="loading-spinner"></div>
          <p>Cargando plantillas...</p>
        </div>
      ) : (
        <>
          <div className="templates-grid">
            <div className="template-card custom" onClick={createCustomBot}>
              <div className="template-custom-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </div>
              <h2>Personalizado</h2>
              <p>Crea tu propio bot desde cero con configuración personalizada.</p>
              <button className="create-btn">Crear Bot</button>
            </div>
            
            {filteredTemplates.map(template => (
              <div 
                key={template.id} 
                className="template-card"
                onClick={() => selectTemplate(template.id)}
              >
                <div className="template-badge">
                  {template.bot_type === 'commercial' && 'Comercial'}
                  {template.bot_type === 'personal' && 'Personal'}
                  {template.bot_type === 'website' && 'Web'}
                </div>
                <h2>{template.name}</h2>
                <p>{template.description}</p>
                
                <div className="template-providers">
                  <div className="provider-group">
                    <h3>Entrada</h3>
                    <div className="provider-chips">
                      {template.input_providers.map(provider => (
                        <span key={`in-${provider}`} className="provider-chip">
                          {provider}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="provider-group">
                    <h3>Acción</h3>
                    <div className="provider-chips">
                      {template.action_providers.map(provider => (
                        <span key={`act-${provider}`} className="provider-chip">
                          {provider}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="provider-group">
                    <h3>Salida</h3>
                    <div className="provider-chips">
                      {template.output_providers.map(provider => (
                        <span key={`out-${provider}`} className="provider-chip">
                          {provider}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <button className="select-btn">Usar Plantilla</button>
              </div>
            ))}
          </div>
          
          {filteredTemplates.length === 0 && (
            <div className="no-templates">
              <p>No se encontraron plantillas que coincidan con los criterios de búsqueda.</p>
              <button className="create-custom-btn" onClick={createCustomBot}>
                Crear Bot Personalizado
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplateSelection;